import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { MarkdownEditor } from '@houseops/markdown-editor';
import '@houseops/markdown-editor/styles.css';
import { useDomains, useCreateGuide } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SlugField, { type SlugStatus } from '@/components/SlugField';

/**
 * Create a new guide. Mirrors ChecklistTemplateNew's layout: sticky
 * action bar at the bottom, full-width markdown editor underneath
 * the metadata column, no surprises.
 */
export default function GuideNew() {
  const navigate = useNavigate();
  const domains = useDomains();
  const create = useCreateGuide();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [domainId, setDomainId] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submitting = create.isPending;
  const canSubmit =
    !submitting &&
    title.trim().length >= 2 &&
    slug.length >= 2 &&
    domainId !== '' &&
    bodyMd.length > 0 &&
    slugStatus !== 'taken' &&
    slugStatus !== 'checking';

  async function onSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    try {
      const guide = await create.mutateAsync({
        slug,
        title: title.trim(),
        domainId,
        bodyMd,
      });
      navigate(`/guides/${guide.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="pb-[calc(13rem+env(safe-area-inset-bottom))] sm:pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-[calc(7.5rem+env(safe-area-inset-bottom))] lg:pb-8"
      >
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/guides" className="hover:text-foreground">
              Guides
            </Link>{' '}
            · New
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            New guide
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            SOPs for things particular to this property. Markdown — paste from anywhere; it'll
            just work.
          </p>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <section className="space-y-5" aria-labelledby="meta">
            <h2
              id="meta"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Metadata
            </h2>

            <div className="space-y-1.5">
              <Label
                htmlFor="title-input"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Title
              </Label>
              <Input
                id="title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-12 text-base"
                data-testid="new-title"
                required
              />
            </div>

            <SlugField
              source={title}
              value={slug}
              onChange={setSlug}
              onStatusChange={setSlugStatus}
            />

            <div className="space-y-1.5">
              <Label
                htmlFor="domain-select"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Domain
              </Label>
              <select
                id="domain-select"
                value={domainId}
                onChange={(e) => setDomainId(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="new-domain"
                required
              >
                <option value="">Pick a domain…</option>
                {(domains.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="space-y-3" aria-labelledby="body">
            <h2
              id="body"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Body
            </h2>
            <p className="text-sm text-muted-foreground">
              Headings, bullets, links, code — anything Markdown does. Tap the formatting bar
              for shortcuts; ⌘/Ctrl + B / I / E / K work too.
            </p>
            <div data-testid="new-body">
              <MarkdownEditor
                value={bodyMd}
                onChange={setBodyMd}
                minHeight="60vh"
                toolbar="auto"
                maxLength={200_000}
                onSave={() => onSubmit()}
              />
            </div>
          </section>
        </div>

        {error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        <div
          className={
            'fixed inset-x-0 z-30 border-t border-rule bg-background/95 ' +
            'shadow-[0_-12px_24px_-12px_rgb(0_0_0_/_0.12)] backdrop-blur ' +
            'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] ' +
            'md:bottom-0 md:pb-[env(safe-area-inset-bottom)] md:shadow-none ' +
            'lg:static lg:mt-8 lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:pb-0 lg:shadow-none'
          }
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-end lg:px-0">
            <div className="flex items-center gap-2 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/guides')}
                className="h-12 px-3 text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="h-12 flex-1 sm:flex-initial sm:px-6"
                data-testid="new-submit"
              >
                {submitting ? 'Saving…' : 'Create guide'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
